import {
  command,
  CommandFunction,
  EditorState,
  extension,
  PlainExtension,
} from 'remirror';

export const PAUSE_TOKEN = /^[.,!?;:-]\s$/;

/*
 * Provides commands for interacting with pause cues in the editor
 */
@extension({
  defaultOptions: {},
  staticKeys: [],
  handlerKeys: [],
  customHandlerKeys: [],
})
class PauseCueCommandsExtension extends PlainExtension {
  get name() {
    return 'pauseCueCommandsExtension' as const;
  }

  /*
   * to - from should be greater than 1
   * text between from and to should be within a valid pause token [.,!?;:-]\s
   */
  private clampToPauseTokenBoundaries(
    state: EditorState,
    from: number,
    to: number
  ) {
    const selectedLength = to - from;

    if (selectedLength === 1) {
      const char = state.doc.textBetween(from, to, '\n', '\n');

      if (isWhitespace(char)) return [from - 1, to];
      if (isPausePunctuation(char)) return [from, to + 1];
    }

    return [from, to];
  }

  @command()
  setPauseCue(value: string): CommandFunction {
    return ({ state, tr, dispatch }) => {
      const { from, to, empty } = state.selection;
      if (empty) return false;

      if (!isPauseTokenSelected(state)) return false;

      const [f, t] = this.clampToPauseTokenBoundaries(state, from, to);

      tr.addMark(f, t, state.schema.marks.pause.create({ value }));

      dispatch?.(tr.scrollIntoView());
      return true;
    };
  }

  @command()
  removePauseCue(): CommandFunction {
    return ({ state, tr, dispatch }) => {
      const { from, to, empty } = state.selection;
      if (empty) return false;

      const [f, t] = this.clampToPauseTokenBoundaries(state, from, to);

      tr.removeMark(f, t, state.schema.marks.pause);

      dispatch?.(tr.scrollIntoView());
      return true;
    };
  }
}

const isWhitespace = (char: string) => {
  return /\s/.test(char);
};

const isPausePunctuation = (char: string) => {
  return /[.,!?;:-]/.test(char);
};

const isPauseToken = (str: string) => {
  return PAUSE_TOKEN.test(str);
};

/*
 * returns false if there are any non-whitespace characters after the given
 * position and returns true otherwise
 */
const isEndOfDocument = (state: EditorState, pos: number) => {
  const max = state.doc.nodeSize - 2;

  for (let currentPos = pos; currentPos < max; currentPos += 1) {
    const char = state.doc.textBetween(currentPos, currentPos + 1);
    if (char && !isWhitespace(char)) {
      return false;
    }
  }

  return true;
};

/*
 * Editor Helper that detects if the user selection is within or is a
 * valid pause token: {pausePunctuation}{whitespace}
 * cases currently not considered for simplicity
 *     - punctuation at the end of a paragraph
 *     - punctuation and then a quotation mark e.g. "?' "
 *     - selecting following whitespace after this pattern {punctuation}{whitespace}
 */
export const isPauseTokenSelected = (state: EditorState): boolean => {
  const { from, to, empty } = state.selection;
  const selectedLength = to - from;
  const textSelected = state.doc.textBetween(from, to, '\n', '\n');

  if (empty) return false;

  if (isEndOfDocument(state, to)) return false;

  if (selectedLength === 1) {
    const char = textSelected;

    if (isWhitespace(char)) {
      const before = state.doc.textBetween(Math.max(0, from - 1), from);
      return isPausePunctuation(before);
    }

    if (isPausePunctuation(char)) {
      const after = state.doc.textBetween(to, to + 1);
      return isWhitespace(after);
    }

    // neither whitespace nor a valid pause punctuation character
    return false;
  }

  return isPauseToken(textSelected);
};

/*
 * Editor Helper that converts:
 * <tempo><loudness>punctuation</loudness></tempo> to <pause>punctuation</pause>
 *
 * Abstracts pause cues for remirror, so cue precedence can be handled easily.
 * Requires no other tags to appear w/in a pause cue. This shouldn't occur -- it
 * is prevented in the editor by the pause mark excluding all other cue marks.
 */
export const insertPauseTags = (html: string) => {
  const tempoLoudnessPattern = new RegExp(
    [
      '<tempo\\s+value="(-?\\d+\\.?\\d*)">', // capture tempo value as group 1
      '<loudness\\s+value="-?\\d+\\.?\\d*">',
      '([.,!?;:-]\\s)', // capture content as group 2
      '</loudness></tempo>',
    ].join(''),
    'g'
  );

  return html.replace(tempoLoudnessPattern, '<pause value="$1">$2</pause>');
};

/*
 * Editor Helper that returns the pause mark value from the current selection
 */
export function getPauseCueValue(state: EditorState): number | null {
  const { from, to, empty } = state.selection;

  if (empty) return null;

  let stringValue: string | null = null;

  // Look for the pause mark and assign the value to stringValue
  state.doc.nodesBetween(from, to, node => {
    const { isText } = node;

    if (!isText) return true;

    const m = node.marks.find(mm => mm.type === state.schema.marks.pause);

    if (m && m.attrs && m.attrs.value != null) {
      stringValue = m.attrs.value;
      return false;
    }

    return true;
  });

  if (!stringValue) return null;

  const num = parseFloat(stringValue);
  const result = Number.isFinite(num) ? num : 1;

  return result;
}

export default PauseCueCommandsExtension;
